/**
 * Utilitaires pour améliorer le SEO de l'application
 * Fonctions pour optimiser les Core Web Vitals et l'indexation
 */

/**
 * Génère des liens de pagination pour le SEO (rel="next" et rel="prev")
 * Améliore l'indexation des listes paginées (blog, galerie, etc.)
 */
export function setPaginationLinks(currentPage, totalPages, baseUrl) {
  // Supprimer les anciens liens de pagination
  document.querySelectorAll('link[rel="prev"], link[rel="next"]').forEach(el => el.remove());

  // Lien vers la page précédente
  if (currentPage > 1) {
    const prevLink = document.createElement('link');
    prevLink.rel = 'prev';
    prevLink.href = currentPage === 2 ? baseUrl : `${baseUrl}?page=${currentPage - 1}`;
    document.head.appendChild(prevLink);
  }

  // Lien vers la page suivante
  if (currentPage < totalPages) {
    const nextLink = document.createElement('link');
    nextLink.rel = 'next';
    nextLink.href = `${baseUrl}?page=${currentPage + 1}`;
    document.head.appendChild(nextLink);
  }
}

/**
 * Ajoute un preload pour les images critiques (LCP optimization)
 * Améliore le Largest Contentful Paint
 */
export function preloadCriticalImage(imageUrl, imageSrcset = null) {
  const link = document.createElement('link');
  link.rel = 'preload';
  link.as = 'image';
  link.href = imageUrl;
  if (imageSrcset) {
    link.setAttribute('imagesrcset', imageSrcset);
  }
  link.setAttribute('fetchpriority', 'high');
  document.head.appendChild(link);
}

/**
 * Ajoute des attributs d'optimisation des images
 * Améliore le CLS (Cumulative Layout Shift)
 */
export function getOptimizedImageProps(src, alt, width, height) {
  return {
    src,
    alt,
    width,
    height,
    loading: 'lazy',
    decoding: 'async',
    // Calcul du ratio pour réserver l'espace et éviter le CLS
    style: { aspectRatio: width && height ? `${width}/${height}` : undefined },
  };
}

/**
 * Génère un srcset pour images responsives
 * Améliore les performances sur mobile
 */
export function generateSrcSet(baseUrl, widths = [320, 640, 960, 1280, 1920]) {
  return widths
    .map(w => `${baseUrl}?w=${w} ${w}w`)
    .join(', ');
}

/**
 * Génère des breadcrumbs structurés pour le SEO
 * Format compatible avec schema.org
 */
export function generateBreadcrumbSchema(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "item": item.url,
    })),
  };
}

/**
 * Ajoute un attribut rel="noopener noreferrer" aux liens externes
 * Améliore la sécurité et le SEO
 */
export function secureExternalLinks() {
  document.querySelectorAll('a[href^="http"]').forEach(link => {
    const url = new URL(link.href);
    if (url.hostname !== window.location.hostname) {
      link.rel = 'noopener noreferrer';
      link.target = link.target || '_blank';
    }
  });
}

/**
 * Génère des meta tags pour articles de blog (SEO optimisé)
 */
export function generateBlogPostMeta({
  title,
  description,
  author = 'Illumine Studio',
  publishedDate,
  modifiedDate,
  image,
  category,
  tags = [],
  readingTime,
}) {
  return {
    // Open Graph
    'og:type': 'article',
    'og:title': title,
    'og:description': description,
    'og:image': image,
    'article:published_time': publishedDate,
    'article:modified_time': modifiedDate || publishedDate,
    'article:author': author,
    'article:section': category,
    'article:tag': tags,

    // Twitter
    'twitter:card': 'summary_large_image',
    'twitter:title': title,
    'twitter:description': description,
    'twitter:image': image,

    // Méta additionnelles
    'author': author,
    'reading-time': readingTime,
  };
}

/**
 * Génère un JSON-LD pour un produit (SoftwareApplication)
 */
export function generateProductSchema({
  name,
  description,
  image,
  price,
  currency = 'EUR',
  rating,
  reviewCount,
  features = [],
}) {
  const hasAggregateRating = Number.isFinite(Number(rating)) && Number(reviewCount) > 0;

  return {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": name,
    "description": description,
    "image": image,
    "applicationCategory": "DesignApplication",
    "operatingSystem": "Web Browser",
    "offers": {
      "@type": "Offer",
      "price": price,
      "priceCurrency": currency,
      "availability": "https://schema.org/InStock",
    },
    ...(hasAggregateRating && {
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": rating,
        "ratingCount": reviewCount,
        "bestRating": "5",
        "worstRating": "1",
      },
    }),
    "featureList": features.join(", "),
  };
}

/**
 * Optimisation des Core Web Vitals
 * Monitore et rapporte les métriques de performance
 */
export function initWebVitals() {
  // Utilise l'API Performance Observer si disponible
  if ('PerformanceObserver' in window) {
    try {
      // Observer les Layout Shifts (CLS)
      const clsObserver = new PerformanceObserver((list) => {
        let clsScore = 0;
        for (const entry of list.getEntries()) {
          if (!entry.hadRecentInput) {
            clsScore += entry.value;
          }
        }
        // Log ou envoyer à un service d'analytics
        if (clsScore > 0.1) {
          console.warn(`[SEO] CLS score élevé: ${clsScore.toFixed(3)}`);
        }
      });
      clsObserver.observe({ type: 'layout-shift', buffered: true });

      // Observer les Largest Contentful Paint (LCP)
      const lcpObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        const lastEntry = entries[entries.length - 1];
        const lcp = lastEntry.renderTime || lastEntry.loadTime;
        if (lcp > 2500) {
          console.warn(`[SEO] LCP élevé: ${lcp.toFixed(0)}ms`);
        }
      });
      lcpObserver.observe({ type: 'largest-contentful-paint', buffered: true });

      // Observer les First Input Delay (FID)
      const fidObserver = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          const fid = entry.processingStart - entry.startTime;
          if (fid > 100) {
            console.warn(`[SEO] FID élevé: ${fid.toFixed(0)}ms`);
          }
        }
      });
      fidObserver.observe({ type: 'first-input', buffered: true });
    } catch (e) {
      console.error('[SEO] Erreur lors de l\'initialisation des Web Vitals:', e);
    }
  }
}

/**
 * Précharge les ressources critiques pour améliorer le FCP
 * (First Contentful Paint)
 */
export function preloadCriticalResources(resources) {
  resources.forEach(({ href, as, type, crossorigin }) => {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.href = href;
    link.as = as;
    if (type) link.type = type;
    if (crossorigin) link.crossOrigin = 'anonymous';
    link.setAttribute('fetchpriority', 'high');
    document.head.appendChild(link);
  });
}

/**
 * Génère des meta tags pour le partage social optimisé
 */
export function generateSocialShareMeta({
  title,
  description,
  image,
  url,
  type = 'website',
  siteName = 'Illumine Studio',
  twitterHandle = '@illuminestudio',
}) {
  return {
    // Open Graph (Facebook, LinkedIn, etc.)
    'og:type': type,
    'og:title': title,
    'og:description': description,
    'og:image': image,
    'og:image:width': '1200',
    'og:image:height': '630',
    'og:image:alt': title,
    'og:url': url,
    'og:site_name': siteName,
    'og:locale': 'fr_FR',

    // Twitter
    'twitter:card': 'summary_large_image',
    'twitter:title': title,
    'twitter:description': description,
    'twitter:image': image,
    'twitter:image:alt': title,
    'twitter:site': twitterHandle,
    'twitter:creator': twitterHandle,
  };
}

/**
 * Vérifie et corrige les problèmes SEO communs
 */
export function runSEOAudit() {
  const issues = [];

  // Vérifier la présence du title
  if (!document.title || document.title.length < 10) {
    issues.push('Title manquant ou trop court');
  }

  // Vérifier la meta description
  const metaDesc = document.querySelector('meta[name="description"]');
  if (!metaDesc || metaDesc.content.length < 50) {
    issues.push('Meta description manquante ou trop courte');
  }

  // Vérifier les images sans alt
  const imagesWithoutAlt = document.querySelectorAll('img:not([alt])');
  if (imagesWithoutAlt.length > 0) {
    issues.push(`${imagesWithoutAlt.length} image(s) sans attribut alt`);
  }

  // Vérifier les liens internes cassés (404)
  const internalLinks = document.querySelectorAll('a[href^="/"]');
  internalLinks.forEach(link => {
    if (!link.href) {
      issues.push('Lien interne vide détecté');
    }
  });

  // Vérifier la balise canonical
  const canonical = document.querySelector('link[rel="canonical"]');
  if (!canonical) {
    issues.push('Balise canonical manquante');
  }

  // Afficher les résultats
  if (issues.length > 0) {
    console.warn('[SEO Audit] Problèmes détectés:', issues);
  } else {
    console.log('[SEO Audit] ✓ Aucun problème détecté');
  }

  return issues;
}

/**
 * Initialise les optimisations SEO au chargement de la page
 */
export function initSEOOptimizations() {
  // Exécuter au DOMContentLoaded
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      secureExternalLinks();
      initWebVitals();

      // Audit SEO en développement uniquement
      if (import.meta.env.DEV) {
        setTimeout(() => runSEOAudit(), 2000);
      }
    });
  } else {
    secureExternalLinks();
    initWebVitals();

    if (import.meta.env.DEV) {
      setTimeout(() => runSEOAudit(), 2000);
    }
  }
}
